import type { ReactElement } from 'react'
import { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatMinor } from '~/api/client'
import { ChartCard } from '~/charts/ChartCard'
import { isNoData } from '~/charts/data'
import { compactMinor, formatAxisDate, formatSignedMinor } from '~/charts/format'
import { Card, CardHead, StatCard } from '~/components/ui/Card'
import { Filter } from '~/components/ui/Controls'
import { DeltaChip } from '~/components/ui/Figure'
import { StatusPill } from '~/components/ui/Status'
import { DataTable, Swatch } from '~/components/ui/Table'
import { chartCss, seriesColor } from '~/charts/theme'
import { figureFormat, householdLocale } from '~/format/locale'
import type {
  DatedProvenance,
  Measure,
  MonthPerformanceRow,
  PerformanceResponse,
  RunwayResponse,
  TransfersResponse,
} from '~/state/queries'
import {
  useHouseholdShape,
  usePerformance,
  useRunway,
  useTransfers,
  usePrivateHoldings,
} from '~/state/queries'
import { formatSignedBasisPoints, includesPrivateHoldings } from '~/state/private-holdings'
import { figuresFor, shownFigure, spendableAssumption } from '~/state/figures'

const RANGES: { months: number; label: string }[] = [
  { months: 6, label: '6 months' },
  { months: 12, label: '1 year' },
  { months: 24, label: '2 years' },
  { months: 60, label: '5 years' },
]

const SPEND_WINDOWS: { months: number; label: string }[] = [
  { months: 3, label: '3 months' },
  { months: 6, label: '6 months' },
  { months: 12, label: '12 months' },
]

const CONTRIBUTION_SLOT = 0
const RETURN_SLOT = 2

const MONTH_COLUMNS = [
  { id: 'month', label: 'Month', width: '130px' },
  { id: 'opening', label: 'Opening', width: '150px', align: 'right' as const },
  { id: 'contributions', label: 'Contributions', width: '150px', align: 'right' as const },
  { id: 'market', label: 'Market return', width: '150px', align: 'right' as const },
  { id: 'closing', label: 'Closing', width: '150px', align: 'right' as const },
  { id: 'return', label: 'Return', width: '110px', align: 'right' as const },
  { id: 'data', label: 'Data' },
]

/** A month label alone is ambiguous once a range crosses a year boundary. */
const MONTH_SPAN_DAYS = 365

const PAGE_CSS = `
.nw-performance .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); }
.nw-performance .nw-stats > * { flex: 1 1 210px; }
.nw-performance .nw-marks { display: flex; flex-wrap: wrap; gap: var(--nw-space-1); margin-top: var(--nw-space-1); }
.nw-performance .nw-identity { color: var(--nw-ink-2); font-size: var(--nw-text-meta); margin: 0; max-width: 78ch; }
.nw-performance .nw-identity b { color: var(--nw-ink); font-weight: 600; }
/* A month, a bar and an amount are one reading; held to a measure so the amount
   is not a thousand pixels from the month it belongs to. */
.nw-performance .nw-spend { list-style: none; margin: 8px 0 12px; max-width: 620px; padding: 0; }
.nw-performance .nw-spend li { align-items: center; display: flex; font-size: var(--nw-text-cell); gap: 10px; padding: 3px 0; }
.nw-performance .nw-spend span:first-child { color: var(--nw-ink-2); min-width: 64px; }
.nw-performance .nw-spend b { font-variant-numeric: tabular-nums; font-weight: 600; min-width: 96px; text-align: right; }
.nw-performance .nw-spend li[data-used='false'] { color: var(--nw-muted); }
.nw-performance .nw-spend li[data-used='false'] i { opacity: .4; }
`

export function Performance(): ReactElement {
  const { settings } = usePrivateHoldings()
  const [figure, setFigure] = useState<Measure>('lookThrough')
  const figures = figuresFor(useHouseholdShape(settings) ?? { company: false, unsellable: false })
  const shown = shownFigure(figures, figure)
  const assumption = spendableAssumption(shown)
  const [months, setMonths] = useState(12)
  const [spendWindow, setSpendWindow] = useState(3)
  const [view, setView] = useState<'chart' | 'table'>('chart')

  const performance = usePerformance(settings, shown, months)
  const transfers = useTransfers(settings, shown, performance.data?.from)
  const runwayQuery = useRunway(settings, spendWindow)

  const data = performance.data

  return (
    <main className="nw nw-performance">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Performance</h1>
      {/* The dates the figures cover and the currency they are in — the two
          things a reader cannot work out from the title. */}
      {data && (
        <p className="nw-sub">{`${data.from} to ${data.to} · all figures in ${data.baseCurrency}`}</p>
      )}

      <div className="nw-filters">
        <Filter
          label="Figure"
          choices={figures.map((entry) => ({ id: entry.key, label: entry.label }))}
          value={shown}
          onChange={(id) => setFigure(id as Measure)}
        />
        <Filter
          label="Range"
          choices={RANGES.map((range) => ({ id: String(range.months), label: range.label }))}
          value={String(months)}
          onChange={(id) => setMonths(Number(id))}
        />
      </div>

      {assumption !== null && <p className="nw-note">{assumption}</p>}

      {performance.isPending && <p className="nw-empty">Loading…</p>}

      {performance.isError && (
        <p className={isNoData(performance.error) ? 'nw-empty' : 'nw-flag nw-flag--missing'}>
          {isNoData(performance.error) ? (
            'No snapshot has been taken yet, so there is nothing to decompose.'
          ) : (
            <>
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>Could not load the monthly figures: {performance.error.message}</span>
            </>
          )}
        </p>
      )}

      {data && (
        <>
          <Headline result={data} />

          <RangeProvenance days={data.provenance} />

          <ChartCard
            title="Contributions and market return, by month"
            description="Whatever a month's net worth did that its cashflows do not account for is the
              market's doing. The two are measured once and cannot disagree."
            view={view}
            onViewChange={setView}
            legend={[
              { label: 'Contributions', color: seriesColor(CONTRIBUTION_SLOT), shape: 'area' },
              { label: 'Market return', color: seriesColor(RETURN_SLOT), shape: 'area' },
            ]}
          >
            {data.months.length === 0 ? (
              <p className="nw-empty">No month in this range has both ends.</p>
            ) : view === 'chart' ? (
              <div className={performance.isFetching ? 'nw-plot nw-plot--loading' : 'nw-plot'}>
                <ContributionChart months={data.months} baseCurrency={data.baseCurrency} />
              </div>
            ) : (
              <MonthsTable
                months={data.months}
                baseCurrency={data.baseCurrency}
                days={data.provenance}
              />
            )}
          </ChartCard>

          <Netting transfers={transfers.data} />

          {data.missingFx.length > 0 && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                No rate for {data.missingFx.join(', ')} on some days, so those flows are left out of
                the contribution figure rather than counted at par.
              </span>
            </p>
          )}

          {data.months.some((month) => month.partial) && (
            <p className="nw-note">
              A month marked partial is measured from the first date with a snapshot rather than from
              the day before it started, so its return covers less than the whole month.
            </p>
          )}
        </>
      )}

      <Runway
        result={runwayQuery.data}
        isPending={runwayQuery.isPending}
        error={runwayQuery.error}
        window={spendWindow}
        onWindowChange={setSpendWindow}
        countsPrivateHoldings={includesPrivateHoldings(settings, 'runway')}
      />
    </main>
  )
}

/**
 * A signed figure as a chip.
 *
 * The bar that grew out of a zero tick belonged to a dark, monospaced direction
 * that is no longer being built; the chosen design says the same thing with the
 * chip's shape, so nothing here scales against its column any more.
 */
function Delta({
  amountMinor,
  currency,
  format,
}: {
  amountMinor: number
  currency: string
  format?: (amountMinor: number) => string
}): ReactElement {
  const went = amountMinor > 0 ? 'up' : amountMinor < 0 ? 'down' : 'flat'
  return (
    <DeltaChip direction={went}>
      {format ? format(amountMinor) : formatSignedMinor(amountMinor, currency)}
    </DeltaChip>
  )
}

function Headline({ result }: { result: PerformanceResponse }): ReactElement {
  return (
    <>
      <div className="nw-stats">
        <StatCard
          label="Change"
          note={`${formatMinor(result.openingMinor, result.baseCurrency)} to ${formatMinor(result.closingMinor, result.baseCurrency)}`}
        >
          <Delta amountMinor={result.changeMinor} currency={result.baseCurrency} />
        </StatCard>
        <StatCard label="Contributions" note="Money that crossed the boundary. The part you did.">
          <span className="nw-measure">
            <Swatch color={seriesColor(CONTRIBUTION_SLOT)} />
            <Delta amountMinor={result.contributionsMinor} currency={result.baseCurrency} />
          </span>
        </StatCard>
        <StatCard label="Market return" note="The part the world did.">
          <span className="nw-measure">
            <Swatch color={seriesColor(RETURN_SLOT)} />
            <Delta amountMinor={result.marketReturnMinor} currency={result.baseCurrency} />
          </span>
        </StatCard>
        <StatCard
          label="Return"
          note="On the capital actually at work, weighted by when it arrived."
        >
          {result.returnBp === null ? (
            '—'
          ) : (
            <Delta
              amountMinor={result.returnBp}
              currency={result.baseCurrency}
              format={formatSignedBasisPoints}
            />
          )}
        </StatCard>
      </div>

      <p className="nw-identity">
        <b>{formatSignedMinor(result.changeMinor, result.baseCurrency)}</b> since {result.from} ={' '}
        <b>{formatSignedMinor(result.contributionsMinor, result.baseCurrency)}</b> put in{' '}
        {result.marketReturnMinor < 0 ? 'less' : 'plus'}{' '}
        <b>
          {formatMinor(Math.abs(result.marketReturnMinor), result.baseCurrency)}
        </b>{' '}
        the market {result.marketReturnMinor < 0 ? 'took' : 'gave'}.
      </p>
    </>
  )
}

function RangeProvenance({ days }: { days: readonly DatedProvenance[] }): ReactElement | null {
  const projected = days.filter((day) => day.projected).length
  const stale = days.filter((day) => day.stale)
  const staleSince = stale.reduce<string | null>(
    (soonest, day) => earliest(soonest, day.staleSince),
    null,
  )

  if (projected === 0 && stale.length === 0) return null

  return (
    <>
      {projected > 0 && (
        <p className="nw-flag nw-flag--modelled">
          <span className="nw-flag-icon" aria-hidden="true">
            ◆
          </span>
          <span>
            <b>Modelled</b> — {projected} of the {days.length} days measured rest on positions no
            source confirmed, so the change and everything split out of it rest on them too.
          </span>
        </p>
      )}

      {stale.length > 0 && (
        <p className="nw-flag nw-flag--stale">
          <span className="nw-flag-icon" aria-hidden="true">
            ▲
          </span>
          <span>
            <b>Carried forward</b> — {stale.length} of the {days.length} days measured repeat an
            earlier day's positions; nothing has reported since {staleSince ?? 'an unknown date'}.
          </span>
        </p>
      )}
    </>
  )
}

function earliest(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return a < b ? a : b
}

function ContributionChart({
  months,
  baseCurrency,
}: {
  months: readonly MonthPerformanceRow[]
  baseCurrency: string
}): ReactElement {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={months as MonthPerformanceRow[]}
        margin={{ top: 8, right: 34, bottom: 0, left: 8 }}
        barGap={2}
        barCategoryGap="26%"
      >
        <CartesianGrid stroke="var(--nw-grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="month"
          axisLine={{ stroke: 'var(--nw-axis)' }}
          tickLine={false}
          minTickGap={16}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={(month: string) => formatAxisDate(`${month}-01`, MONTH_SPAN_DAYS)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={72}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={compactMinor}
        />
        <ReferenceLine y={0} stroke="var(--nw-axis)" strokeWidth={1} />
        <Tooltip
          cursor={{ fill: 'var(--nw-plane)' }}
          content={({ active, payload }) =>
            active ? (
              <MonthTooltip
                month={payload?.[0]?.payload as MonthPerformanceRow | undefined}
                baseCurrency={baseCurrency}
              />
            ) : null
          }
        />
        <Bar
          dataKey="contributionsMinor"
          name="Contributions"
          fill={seriesColor(CONTRIBUTION_SLOT)}
          radius={2}
          isAnimationActive={false}
        />
        <Bar
          dataKey="marketReturnMinor"
          name="Market return"
          fill={seriesColor(RETURN_SLOT)}
          radius={2}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

function MonthTooltip({
  month,
  baseCurrency,
}: {
  month: MonthPerformanceRow | undefined
  baseCurrency: string
}): ReactElement | null {
  if (!month) return null

  return (
    <div className="nw-tooltip">
      <h3>
        {month.month}
        {month.partial ? ' · partial' : ''}
      </h3>
      <dl>
        <div>
          <dt>
            <span className="nw-key" style={{ background: seriesColor(CONTRIBUTION_SLOT) }} />
          </dt>
          <dd className="nw-tooltip-name">Contributions</dd>
          <dd className="nw-tooltip-value">
            {formatSignedMinor(month.contributionsMinor, baseCurrency)}
          </dd>
        </div>
        <div>
          <dt>
            <span className="nw-key" style={{ background: seriesColor(RETURN_SLOT) }} />
          </dt>
          <dd className="nw-tooltip-name">Market return</dd>
          <dd className="nw-tooltip-value">
            {formatSignedMinor(month.marketReturnMinor, baseCurrency)}
          </dd>
        </div>
        <div>
          <dt />
          <dd className="nw-tooltip-name">Change</dd>
          <dd className="nw-tooltip-value">{formatSignedMinor(month.changeMinor, baseCurrency)}</dd>
        </div>
        <div>
          <dt />
          <dd className="nw-tooltip-name">Return</dd>
          <dd className="nw-tooltip-value">
            {month.returnBp === null ? '—' : formatSignedBasisPoints(month.returnBp)}
          </dd>
        </div>
      </dl>
    </div>
  )
}

function MonthsTable({
  months,
  baseCurrency,
  days,
}: {
  months: readonly MonthPerformanceRow[]
  baseCurrency: string
  days: readonly DatedProvenance[]
}): ReactElement {
  return (
    <div className="nw-table-wrap">
      <DataTable
        dense
        label={`Contributions and market return by month, in ${baseCurrency}`}
        columns={MONTH_COLUMNS}
      >
        {[...months].reverse().map((month) => {
            const span = days.filter(
              (day) => day.date >= month.openingDate && day.date <= month.closingDate,
            )
            const projected = span.some((day) => day.projected)
            const stale = span.some((day) => day.stale)
            return (
              <tr key={month.month} data-flagged={month.partial || projected || stale}>
                <th scope="row">
                  <span className="nw-measure">
                    {month.month}
                    {/* A month that is not over yet has not failed. */}
                    {month.partial && <StatusPill tone="caution" label="partial" />}
                  </span>
                </th>
                <td className="nw-num">{formatMinor(month.openingMinor, baseCurrency)}</td>
                <td className="nw-num">
                  <Delta amountMinor={month.contributionsMinor} currency={baseCurrency} />
                </td>
                <td className="nw-num">
                  <Delta amountMinor={month.marketReturnMinor} currency={baseCurrency} />
                </td>
                <td className="nw-num">{formatMinor(month.closingMinor, baseCurrency)}</td>
                <td className="nw-num">
                  {month.returnBp === null ? (
                    '—'
                  ) : (
                    <Delta
                      amountMinor={month.returnBp}
                      currency={baseCurrency}
                      format={formatSignedBasisPoints}
                    />
                  )}
                </td>
                <td>
                  {projected && 'Modelled'}
                  {projected && stale && ' · '}
                  {stale && 'Carried forward'}
                  {!projected && !stale && 'Fetched'}
                </td>
              </tr>
            )
          })}
      </DataTable>
    </div>
  )
}

/**
 * Moving money from one tracked account to another is not saving, so the pair is
 * cancelled. A leg whose partner was never found is still counted, which is the
 * one way this figure can flatter itself.
 */
function Netting({ transfers }: { transfers: TransfersResponse | undefined }): ReactElement | null {
  if (!transfers) return null

  const movedMinor = transfers.transfers.reduce((total, row) => total + row.amountMinor, 0)
  const residualMinor = transfers.transfers.reduce((total, row) => total + row.residualMinor, 0)

  return (
    <>
      {transfers.transfers.length > 0 && (
        <p className="nw-note">
          {transfers.transfers.length} transfer
          {transfers.transfers.length === 1 ? '' : 's'} between your own accounts —{' '}
          {formatMinor(movedMinor, transfers.baseCurrency)} in all — were matched leg to leg and
          left out of the contributions above.
          {residualMinor !== 0 &&
            ` ${formatMinor(residualMinor, transfers.baseCurrency)} of that did not cancel exactly and is counted.`}
        </p>
      )}

      {transfers.unmatched.length > 0 && (
        <p className="nw-flag nw-flag--stale">
          <span className="nw-flag-icon" aria-hidden="true">
            ▲
          </span>
          <span>
            <b>{transfers.unmatched.length} unmatched transfer</b>
            {transfers.unmatched.length === 1 ? '' : 's'} — a leg left an account and nothing
            arrived on another tracked one within the window, so it counts as money in or out. The
            first is {transfers.unmatched[0]?.date}.
          </span>
        </p>
      )}
    </>
  )
}

function Runway({
  result,
  isPending,
  error,
  window: spendWindow,
  onWindowChange,
  countsPrivateHoldings,
}: {
  result: RunwayResponse | undefined
  isPending: boolean
  error: Error | null
  window: number
  onWindowChange: (months: number) => void
  countsPrivateHoldings: boolean
}): ReactElement {
  const peakSpendMinor = Math.max(1, ...(result?.spend ?? []).map((month) => month.spendMinor))

  return (
    <Card className="nw-runway">
      <CardHead
        id="nw-runway-heading"
        title="Runway"
        lede={
          <>
            Liquid assets over what a month actually costs. Private holdings are out of it by
            construction, not by preference
            {countsPrivateHoldings ? '' : ' — the switch cannot put them in'}.
          </>
        }
        aside={
          <Filter
            label="Average over"
            choices={SPEND_WINDOWS.map((entry) => ({
              id: String(entry.months),
              label: entry.label,
            }))}
            value={String(spendWindow)}
            onChange={(id) => onWindowChange(Number(id))}
          />
        }
      />

      {isPending && <p className="nw-empty">Loading…</p>}

      {error && (
        <p className={isNoData(error) ? 'nw-empty' : 'nw-flag nw-flag--missing'}>
          {isNoData(error)
            ? 'Nothing has been held yet, so there is nothing to spend.'
            : `Could not work out the runway: ${error.message}`}
        </p>
      )}

      {result && (
        <>
          <div className="nw-stats">
            <StatCard
              label="Runway"
              note={
                result.runwayMonthsBp === null
                  ? 'No spending in the window, so this cannot be divided.'
                  : `At ${formatMinor(result.averageSpendMinor, result.baseCurrency)} a month.`
              }
            >
              {formatMonths(result.runwayMonthsBp)}
            </StatCard>
            <StatCard
              label="Liquid"
              note={
                <>
                  On {result.date}.
                  {(result.projected || result.stale) && (
                    <span className="nw-marks">
                      {result.projected && <StatusPill tone="caution" label="Projected" />}
                      {result.stale && (
                        <StatusPill
                          tone="failed"
                          label={`Stale${result.staleSince ? ` since ${result.staleSince}` : ''}`}
                        />
                      )}
                    </span>
                  )}
                </>
              }
            >
              {formatMinor(result.liquidMinor, result.baseCurrency)}
            </StatCard>
            <StatCard
              label="Average spend"
              note="Transfers to your own accounts and investment purchases are not spending."
            >
              {formatMinor(result.averageSpendMinor, result.baseCurrency)}
            </StatCard>
          </div>

          {result.projected && (
            <p className="nw-flag nw-flag--modelled">
              <span className="nw-flag-icon" aria-hidden="true">
                ◆
              </span>
              <span>
                <b>Modelled</b> — part of the liquid figure on {result.date} rests on positions no
                source confirmed, so the months it buys are counted against a number that was not
                all fetched.
              </span>
            </p>
          )}

          {result.stale && (
            <p className="nw-flag nw-flag--stale">
              <span className="nw-flag-icon" aria-hidden="true">
                ▲
              </span>
              <span>
                <b>Carried forward</b> — nothing has reported since{' '}
                {result.staleSince ?? 'an unknown date'}, so the liquid figure repeats the last
                known positions rather than counting them as a loss.
              </span>
            </p>
          )}

          <ul className="nw-spend">
            {[...result.spend].reverse().map((month) => (
              <li key={month.month} data-used={result.monthsUsed.includes(month.month)}>
                <span>{month.month}</span>
                <span className="nw-bar">
                  <i
                    style={{
                      background: seriesColor(1),
                      width: `${Math.round((month.spendMinor * 100) / peakSpendMinor)}%`,
                    }}
                  />
                </span>
                <b>{formatMinor(month.spendMinor, result.baseCurrency)}</b>
              </li>
            ))}
          </ul>

          {result.missingFx.length > 0 && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                No rate for {result.missingFx.join(', ')} on some days, so that spending is left out
                of the average rather than counted as nothing spent. The runway above is longer than
                those months paid for.
              </span>
            </p>
          )}

          {!result.complete && (
            <p className="nw-flag nw-flag--stale">
              <span className="nw-flag-icon" aria-hidden="true">
                ▲
              </span>
              <span>
                Only {result.monthsUsed.length} whole month
                {result.monthsUsed.length === 1 ? '' : 's'} of history to average, not {spendWindow}.
                The month in progress is left out — half a month of spending would report a runway
                that grows the closer it is looked at.
              </span>
            </p>
          )}
        </>
      )}
    </Card>
  )
}

function formatMonths(runwayMonthsBp: number | null): string {
  if (runwayMonthsBp === null) return '—'
  const months = figureFormat(householdLocale(), { maximumFractionDigits: 1 })
  return `${months.format(runwayMonthsBp / 10_000)} months`
}
